import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { ensureLeadingSlash } from '@/lib/utils/heatmapUtils'
import { getPages } from '@/lib/api/api'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSiteSafe } from '@/lib/contexts/SiteContext'
import { useWorkspaceSafe } from '@/lib/contexts/WorkspaceContext'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  PlusSignIcon,
  Search01Icon,
  File02Icon,
  Loading01Icon,
  SquareLock02Icon,
  ArrowRight01Icon,
} from '@hugeicons/core-free-icons'

interface AddPageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd: (pageUrl: string, title: string | null) => Promise<void>
  isAdding?: boolean
  /** Heatmap pages currently tracked — used to hide already-tracked suggestions. */
  trackedUrls?: string[]
  /** Plan-limit context (SUPENG-97). When atLimit, the modal shows the upgrade state. */
  used?: number
  limit?: number
  isUnlimited?: boolean
  atLimit?: boolean
  planName?: string
  /** Opens the app's paywall / pricing modal. */
  onUpgrade?: () => void
  /** Site domain, shown as a prefix on the manual-URL input. */
  domain?: string
}

interface SitePage {
  page: string
  title?: string
  visits?: number
}

/** Pull a visit-like count off a /pages row without assuming a single field name. */
function pageVisits(p: any): number | undefined {
  const v = p?.visits ?? p?.views ?? p?.page_views ?? p?.count ?? p?.total
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function normalizePath(url: string): string {
  try {
    return ensureLeadingSlash(url.replace(/^https?:\/\/[^/]+/i, '')).replace(/\/+/g, '/')
  } catch {
    return ensureLeadingSlash(url)
  }
}

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return n.toLocaleString()
}

export function AddPageDialog({
  open,
  onOpenChange,
  onAdd,
  isAdding = false,
  trackedUrls = [],
  used = 0,
  limit = 0,
  isUnlimited = false,
  atLimit = false,
  planName = 'your',
  onUpgrade,
  domain,
}: AddPageDialogProps) {
  const { token } = useAuth()
  const siteContext = useSiteSafe()
  const workspaceContext = useWorkspaceSafe()
  const propertyId = workspaceContext
    ? workspaceContext.selectedPropertyId
    : siteContext?.currentSite?.propertyId || siteContext?.id

  const [query, setQuery] = useState('')
  const [manualUrl, setManualUrl] = useState('')
  const [allPages, setAllPages] = useState<SitePage[]>([])
  const [addingPath, setAddingPath] = useState<string | null>(null)

  useEffect(() => {
    if (open && token && propertyId) loadPages()
    if (!open) {
      setQuery('')
      setManualUrl('')
      setAddingPath(null)
    }
  }, [open, token, propertyId])

  const loadPages = async () => {
    if (!token || !propertyId) return
    try {
      const pages = await getPages(token, propertyId)
      setAllPages(pages || [])
    } catch (error) {
      console.error('Error loading pages:', error)
      setAllPages([])
    }
  }

  // Suggested pages = the site's pages, minus already-tracked, ranked by visits
  // when the API provides them, filtered by the search query.
  const trackedSet = useMemo(
    () => new Set(trackedUrls.map((u) => normalizePath(u))),
    [trackedUrls],
  )

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase()
    return allPages
      .map((p) => ({ path: normalizePath(p.page), title: p.title, visits: pageVisits(p) }))
      .filter((p) => p.path && !trackedSet.has(p.path))
      .filter((p) => !q || p.path.toLowerCase().includes(q))
      .sort((a, b) => (b.visits ?? 0) - (a.visits ?? 0))
      .slice(0, 8)
  }, [allPages, trackedSet, query])

  const remaining = Math.max(0, limit - used)
  const showUsage = !isUnlimited && limit > 0

  const handleAddSuggestion = async (path: string, title?: string) => {
    setAddingPath(path)
    try {
      await onAdd(ensureLeadingSlash(path), title?.trim() || null)
    } finally {
      setAddingPath(null)
    }
  }

  const handleAddManual = async () => {
    const trimmed = manualUrl.trim()
    if (!trimmed) return
    await onAdd(ensureLeadingSlash(normalizePath(trimmed)), null)
    setManualUrl('')
  }

  // ── Segmented usage meter (mirrors the header meter) ──────────────────────
  const segments = showUsage
    ? Array.from({ length: Math.min(limit, 12) }).map((_, i) => i < used)
    : []

  const upgradeState = atLimit && !isUnlimited

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden">
        {/* Header (DialogContent renders its own close button, top-right) */}
        <div className="flex items-start gap-3 px-6 pt-6 pb-4 pr-12">
          <span className="flex shrink-0 size-9 items-center justify-center rounded-[10px] bg-muted text-foreground/70">
            <HugeiconsIcon icon={upgradeState ? SquareLock02Icon : PlusSignIcon} size={19} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground">
              {upgradeState ? `You've reached the ${planName} plan's heatmaps limit` : 'Add a page to track'}
            </h2>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              {upgradeState
                ? `Your plan includes ${limit} heatmaps. This is a plan limit, not an error. Upgrade or free up a slot to add more.`
                : 'Pick a page to capture screenshots and analyze click & scroll behavior.'}
            </p>
          </div>
        </div>

        {/* Usage strip */}
        {showUsage && (
          <div className="flex items-center gap-3 mx-6 mb-1 px-3.5 py-2.5 bg-muted/50 border border-border/60 rounded-xl">
            <div className="flex-1 flex gap-1.5">
              {segments.map((filled, i) => (
                <span
                  key={i}
                  className={cn(
                    'h-2 flex-1 rounded-full transition-colors',
                    filled ? (atLimit ? 'bg-destructive' : 'bg-primary') : 'bg-muted-foreground/20',
                  )}
                />
              ))}
            </div>
            <span className="text-xs font-semibold text-foreground tabular-nums whitespace-nowrap">
              {used} / {limit} used
            </span>
            <span
              className={cn(
                'text-[11.5px] font-semibold whitespace-nowrap',
                atLimit ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {atLimit ? '0 left' : `${remaining} left`}
            </span>
          </div>
        )}

        {upgradeState ? (
          /* ── UPGRADE STATE ── */
          <div className="px-6 pt-4 pb-6">
            <div className="grid grid-cols-2 gap-3 mb-[18px]">
              <div className="p-4 border border-border rounded-xl bg-muted/40">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-foreground">{planName}</span>
                  <span className="text-xs font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                    Current
                  </span>
                </div>
                <div className="text-2xl font-semibold text-foreground mt-2 mb-0.5 tracking-tight">
                  {limit} <span className="text-[13px] font-medium text-muted-foreground">heatmaps</span>
                </div>
                <div className="text-[11.5px] text-destructive font-semibold">All in use</div>
              </div>
              <div className="relative p-4 border-[1.5px] border-foreground rounded-xl bg-muted/40">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-foreground">Scale</span>
                  <span className="text-xs font-semibold text-primary-foreground bg-primary px-1.5 py-0.5 rounded-full">
                    Recommended
                  </span>
                </div>
                <div className="text-2xl font-semibold text-primary mt-2 mb-0.5 tracking-tight">
                  Unlimited <span className="text-[13px] font-medium text-muted-foreground">heatmaps</span>
                </div>
                <div className="text-[11.5px] text-muted-foreground">+ unlimited A/B tests &amp; funnels</div>
              </div>
            </div>
            <Button
              onClick={() => onUpgrade?.()}
              className="w-full h-12 gap-2 text-sm font-semibold"
            >
              Upgrade to Scale
              <HugeiconsIcon icon={ArrowRight01Icon} size={15} />
            </Button>
            <div className="text-center mt-3">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="text-[12.5px] font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Or remove a tracked page to free up a slot
              </button>
            </div>
          </div>
        ) : (
          /* ── LIST STATE ── */
          <>
            {/* Search */}
            <div className="px-6 pt-3.5">
              <div className="relative">
                <HugeiconsIcon
                  icon={Search01Icon}
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search your pages by path…"
                  className="w-full text-[13.5px] text-foreground bg-card border border-border rounded-[10px] pl-9 pr-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
                />
              </div>
            </div>

            {/* Suggestions */}
            <div className="flex-1 overflow-y-auto max-h-[42vh] px-6 pt-3.5 pb-1.5">
              <div className="text-xs font-semibold text-muted-foreground mb-2.5">
                {query.trim() ? 'Matching pages' : 'Suggested · most visited, not yet tracked'}
              </div>
              {suggestions.length > 0 ? (
                <div className="flex flex-col gap-0.5">
                  {suggestions.map((s) => (
                    <div
                      key={s.path}
                      className="flex items-center gap-3 px-2.5 py-2.5 rounded-[9px] hover:bg-muted/60 transition-colors"
                    >
                      <span className="flex shrink-0 size-[30px] items-center justify-center rounded-lg bg-muted/60 border border-border text-muted-foreground">
                        <HugeiconsIcon icon={File02Icon} size={15} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[13px] font-semibold text-foreground truncate">
                          {s.path}
                        </div>
                        {s.visits != null ? (
                          <div className="text-[11px] text-muted-foreground mt-px">
                            {fmtCompact(s.visits)} visits
                          </div>
                        ) : s.title ? (
                          <div className="text-[11px] text-muted-foreground mt-px truncate">{s.title}</div>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddSuggestion(s.path, s.title)}
                        disabled={isAdding || addingPath === s.path}
                        className="shrink-0 h-8 gap-1.5 border-input bg-background hover:bg-muted"
                      >
                        {addingPath === s.path ? (
                          <HugeiconsIcon icon={Loading01Icon} size={13} className="animate-spin" />
                        ) : (
                          <HugeiconsIcon icon={PlusSignIcon} size={13} />
                        )}
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center text-[12.5px] text-muted-foreground">
                  {allPages.length === 0
                    ? 'No pages found for this site yet. Add one as a custom URL below.'
                    : `No tracked-site pages match “${query}”. Add it as a custom URL below.`}
                </div>
              )}
            </div>

            {/* Manual URL */}
            <div className="px-6 pt-3.5 pb-5 border-t border-border mt-1.5">
              <div className="text-xs font-semibold text-muted-foreground mb-2.5">
                Or add any URL
              </div>
              <div className="flex gap-2">
                <div className="flex-1 flex items-center bg-card border border-border rounded-[10px] overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition">
                  {domain && (
                    <span className="text-[13px] text-muted-foreground pl-3 pr-1 whitespace-nowrap">
                      {domain}
                    </span>
                  )}
                  <input
                    value={manualUrl}
                    onChange={(e) => setManualUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddManual()
                    }}
                    placeholder="/your-page"
                    className="flex-1 font-mono text-[13px] text-foreground bg-transparent border-none py-2.5 pr-3 pl-1 outline-none min-w-0"
                  />
                </div>
                <Button
                  onClick={handleAddManual}
                  disabled={isAdding || !manualUrl.trim()}
                  className="shrink-0 h-11 gap-1.5"
                >
                  {isAdding && !addingPath ? (
                    <HugeiconsIcon icon={Loading01Icon} size={15} className="animate-spin" />
                  ) : null}
                  Add page
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
